import React, { useEffect, useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import api, { API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ArrowLeft } from "lucide-react";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import usePageMeta from "@/hooks/usePageMeta";
import PasswordStrength from "@/components/PasswordStrength";
import { getRecaptchaToken } from "@/lib/recaptcha";
import { safeHtml } from "@/lib/sanitize";
import useRecaptcha from "@/hooks/useRecaptcha";
import BrandLogo from "@/components/BrandLogo";

export default function LoginPage({ mode: initialMode = "login" }) {
  const [mode, setMode] = useState(initialMode);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [loading, setLoading] = useState(false);
  // Google-first: hide the email form until the user opts in
  const [showEmailForm, setShowEmailForm] = useState(false);
  // Connexion Google : masquée tant que le serveur n'a pas ses identifiants OAuth (GOOGLE_CLIENT_ID/SECRET)
  const [googleEnabled, setGoogleEnabled] = useState(true);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectTo = searchParams.get("redirect") || "/dashboard";
  const { loginWithToken } = useAuth();
  const { t } = useTranslation();
  useRecaptcha();

  useEffect(() => {
    api.get("/auth/providers").then(({ data }) => setGoogleEnabled(!!data.google)).catch(() => {});
    if (searchParams.get("google_error")) toast.error(t("auth.googleFailed"));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  usePageMeta({
    title:
      mode === "register" ? t("seo.registerTitle") : t("seo.loginTitle"),
    description:
      mode === "register"
        ? t("seo.registerDesc")
        : t("seo.loginDesc"),
    canonical:
      mode === "register"
        ? "https://flowgantt.eu/register"
        : "https://flowgantt.eu/login",
    // Register kept indexable (top of conversion funnel).
    // Login page is a private-doorway page → noindex to avoid SERP dilution.
    noindex: mode === "login",
  });

  const handleGoogle = () => {
    // OAuth Google géré par notre API : /api/auth/google/start puis retour sur le chemin demandé.
    const targetPath = redirectTo.startsWith("/") ? redirectTo : "/dashboard";
    window.location.href = `${API}/auth/google/start?redirect=${encodeURIComponent(targetPath)}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (mode === "register" && !acceptTerms) {
      toast.error(t("auth.termsRequired"));
      return;
    }
    if (mode === "register" && password.length < 12) {
      toast.error(t("auth.passwordTooShort") || "Le mot de passe doit contenir au moins 12 caractères");
      return;
    }
    setLoading(true);
    try {
      let recaptchaToken = "";
      if (mode === "register") {
        recaptchaToken = await getRecaptchaToken("register");
      }
      const endpoint = mode === "register" ? "/auth/register" : "/auth/login";
      const body =
        mode === "register"
          ? { email, password, name, accepted_terms: true, recaptcha_token: recaptchaToken }
          : { email, password };
      const { data } = await api.post(endpoint, body);
      // New activation flow: no token returned, prompt user to check inbox
      if (data.requires_activation) {
        if (data.email_sent === false) {
          toast.error(t("auth.activationEmailFailed"));
        } else {
          toast.success(
            t("auth.checkInbox") ||
              "Vérifie ta boîte mail — un email d'activation vient d'être envoyé."
          );
        }
        navigate(`/activate?email=${encodeURIComponent(email)}`);
        return;
      }
      loginWithToken(data.token, data.user);
      toast.success(mode === "register" ? t("auth.signupSuccess") : t("auth.loginSuccess"));
      navigate(redirectTo);
    } catch (err) {
      toast.error(err.response?.data?.detail || t("common.error"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-white">
      {/* Left side - form */}
      <div className="flex flex-col p-8 lg:p-16">
        <div className="flex items-center justify-between mb-16">
          <Link
            to="/"
            data-testid="back-home-link"
            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-neutral-500 hover:text-neutral-900 transition-colors"
          >
            <ArrowLeft className="w-3 h-3" />
            {t("common.back")}
          </Link>
          <LanguageSwitcher />
        </div>

        <div className="max-w-sm w-full mx-auto flex-1 flex flex-col justify-center">
          <div className="mb-10">
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#E05236] font-semibold mb-3">
              {mode === "register" ? t("auth.createAccount") : t("auth.welcomeBack")}
            </div>
            <h1 className="font-display text-4xl font-black tracking-tighter">
              {mode === "register" ? t("auth.signupTitle") : t("auth.loginTitle")}
            </h1>
            {mode === "register" && (
              <div
                data-testid="reverse-trial-banner"
                className="mt-4 inline-flex items-center gap-2 text-xs font-medium text-[#4A5D4E] bg-emerald-50 border border-emerald-200 px-3 py-1.5 rounded-sm"
              >
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                {t("auth.trialBanner")}
              </div>
            )}
          </div>

          {googleEnabled && (
          <>
          <Button
            data-testid="google-login-btn"
            onClick={handleGoogle}
            className="w-full h-14 rounded-sm bg-white text-neutral-950 border-2 border-neutral-950 hover:bg-neutral-950 hover:text-white transition-colors font-semibold text-base group"
          >
            <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
            </svg>
            {mode === "register" ? t("auth.signupWithGoogle") : t("auth.continueWithGoogle")}
          </Button>
          <p className="text-[11px] text-neutral-500 text-center mt-2">
            {t("auth.googleReassurance")}
          </p>

          <button
            type="button"
            data-testid="toggle-email-form"
            onClick={() => setShowEmailForm((v) => !v)}
            className="text-xs uppercase tracking-[0.2em] text-neutral-500 hover:text-neutral-900 mt-6 mb-4 text-center w-full"
          >
            {showEmailForm ? t("auth.hideEmailForm") : t("auth.showEmailForm")}
          </button>
          </>
          )}

          {(showEmailForm || !googleEnabled) && (
          <>
          <div className="flex items-center gap-4 mb-6">
            <div className="flex-1 h-px bg-stone-200" />
            <span className="text-[10px] uppercase tracking-[0.25em] text-neutral-400">
              {t("auth.orEmail")}
            </span>
            <div className="flex-1 h-px bg-stone-200" />
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {mode === "register" && (
              <div>
                <Label htmlFor="name" className="text-xs uppercase tracking-[0.15em] text-neutral-600">
                  {t("auth.name")}
                </Label>
                <Input
                  id="name"
                  data-testid="signup-name-input"
                  autoComplete="name"
                  autoCapitalize="words"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  className="mt-2 h-11 rounded-sm border-stone-300"
                />
              </div>
            )}
            <div>
              <Label htmlFor="email" className="text-xs uppercase tracking-[0.15em] text-neutral-600">
                {t("auth.email")}
              </Label>
              <Input
                id="email"
                type="email"
                data-testid="auth-email-input"
                autoComplete={mode === "register" ? "email" : "username"}
                inputMode="email"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="mt-2 h-11 rounded-sm border-stone-300"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-xs uppercase tracking-[0.15em] text-neutral-600">
                {t("auth.password")}
              </Label>
              <Input
                id="password"
                type="password"
                data-testid="auth-password-input"
                autoComplete={mode === "register" ? "new-password" : "current-password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={mode === "register" ? 12 : 6}
                className="mt-2 h-11 rounded-sm border-stone-300"
              />
              {mode === "register" && (
                <div className="mt-2">
                  <PasswordStrength value={password} />
                </div>
              )}
            </div>
            {mode === "register" && (
              <label
                htmlFor="accept-terms"
                data-testid="accept-terms-label"
                className="flex items-start gap-2.5 cursor-pointer group pt-1"
              >
                <input
                  id="accept-terms"
                  type="checkbox"
                  data-testid="accept-terms-checkbox"
                  checked={acceptTerms}
                  onChange={(e) => setAcceptTerms(e.target.checked)}
                  required
                  className="mt-0.5 w-4 h-4 accent-[#E05236] cursor-pointer shrink-0"
                />
                <span className="text-xs text-neutral-600 leading-snug select-none">
                  <span dangerouslySetInnerHTML={safeHtml(
                    t("auth.acceptTerms", {
                      cgu: `<a href="/legal/mentions" target="_blank" rel="noopener" data-testid="terms-cgu-link" class="underline underline-offset-2 hover:text-neutral-950 font-medium">${t("auth.cguLink")}</a>`,
                      cgv: `<a href="/legal/cgv" target="_blank" rel="noopener" data-testid="terms-cgv-link" class="underline underline-offset-2 hover:text-neutral-950 font-medium">${t("auth.cgvLink")}</a>`,
                      privacy: `<a href="/legal/confidentialite" target="_blank" rel="noopener" data-testid="terms-privacy-link" class="underline underline-offset-2 hover:text-neutral-950 font-medium">${t("auth.privacyLink")}</a>`,
                    })
                  )} />
                </span>
              </label>
            )}
            <Button
              type="submit"
              data-testid="auth-submit-btn"
              disabled={loading || (mode === "register" && !acceptTerms)}
              className="w-full h-12 rounded-sm bg-neutral-950 hover:bg-neutral-800 text-white mt-2 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {loading ? "..." : mode === "register" ? t("auth.signupBtn") : t("auth.loginBtn")}
            </Button>
          </form>
          </>
          )}

          <div className="mt-8 text-center text-sm text-neutral-600">
            {mode === "register" ? t("auth.hasAccount") : t("auth.noAccount")}{" "}
            <button
              data-testid="toggle-auth-mode"
              onClick={() => setMode(mode === "register" ? "login" : "register")}
              className="font-semibold text-[#E05236] hover:underline"
            >
              {mode === "register" ? t("auth.toggleToLogin") : t("auth.toggleToSignup")}
            </button>
          </div>
        </div>
      </div>

      {/* Right side - visual */}
      <div className="hidden lg:block relative bg-neutral-950 text-white overflow-hidden">
        <div className="absolute inset-0 p-16 flex flex-col justify-between">
          <div>
            <div className="flex items-center gap-2">
              <BrandLogo className="w-7 h-7" />
              <span className="font-display font-black text-lg tracking-tighter">
                FLOWGANTT
              </span>
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-neutral-500 mb-4">
              {t("auth.quoteLabel")}
            </div>
            <blockquote className="font-display text-3xl font-bold leading-tight tracking-tight">
              {t("auth.quoteText")}
              <span className="text-[#E05236]"> {t("auth.quoteHighlight")}</span>
            </blockquote>
            <div className="mt-6 text-sm text-neutral-400">
              {t("auth.quoteAuthor")}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
